import { AGENDA_END_HOUR, AGENDA_INTERVAL_MINUTES, AGENDA_START_HOUR, AGENDA_TOTAL_MINUTES } from './agendaConfig';
import { agendaDateKey } from './agendaPresentation';

export type AgendaMoveTarget = {
  date: string;
  minute: number;
  columnIndex: number;
  columnCount: number;
  practitionerId: string;
  candidates: string[];
};

export const agendaMoveDateTime = (target: Pick<AgendaMoveTarget, 'date' | 'minute'>) =>
  `${target.date}T${String(Math.floor(target.minute / 60)).padStart(2, '0')}:${String(target.minute % 60).padStart(2, '0')}:00`;

export const getAgendaMoveTarget = (
  x: number, y: number, bounds: { left: number; top: number; width: number; height: number },
  columns: readonly { date: Date; practitionerId?: string }[], candidates: readonly string[],
): AgendaMoveTarget | null => {
  if (bounds.width <= 0 || bounds.height <= 0 || !columns.length || x < bounds.left || x >= bounds.left + bounds.width || y < bounds.top || y >= bounds.top + bounds.height) return null;
  const columnIndex = Math.floor((x - bounds.left) / bounds.width * columns.length);
  const column = columns[columnIndex];
  if (!column || (!column.practitionerId && !candidates.length)) return null;
  const minute = AGENDA_START_HOUR * 60 + Math.floor((y - bounds.top) / bounds.height * AGENDA_TOTAL_MINUTES / AGENDA_INTERVAL_MINUTES) * AGENDA_INTERVAL_MINUTES;
  return { date: agendaDateKey(column.date), minute, columnIndex, columnCount: columns.length,
    practitionerId: column.practitionerId || '', candidates: column.practitionerId ? [column.practitionerId] : [...candidates] };
};

export const agendaMoveFitsDay = (target: AgendaMoveTarget, duration: number) =>
  Number.isFinite(duration) && duration > 0 && target.minute >= AGENDA_START_HOUR * 60 && target.minute + duration <= AGENDA_END_HOUR * 60;

// /possible returns suggested starts, not every point inside a free interval.
// Prove an intermediate start by asking the same API to fit its entire covering
// interval. The actual appointment's duration is never changed.
export const agendaMoveCoveringInterval = (slots: readonly number[], minute: number, duration: number) => {
  const anchor = slots.filter((slot) => slot >= AGENDA_START_HOUR * 60 && slot <= minute).sort((a, b) => b - a)[0];
  return anchor === undefined ? null : { anchor, duration: minute - anchor + duration };
};

export const agendaMoveOverlaps = (
  target: AgendaMoveTarget, duration: number, appointmentId: string, practitionerUserId: string,
  appointments: readonly { id: string; when: string; duration: number; practitionerUserId: string }[],
) => {
  const start = new Date(agendaMoveDateTime(target)).getTime(), end = start + duration * 60000;
  return appointments.some((appointment) => appointment.id !== appointmentId && appointment.practitionerUserId === practitionerUserId
    && new Date(appointment.when).getTime() < end
    && new Date(appointment.when).getTime() + appointment.duration * 60000 > start);
};
